import { isObject } from './helpers';
import {
  getAccreditationLabel,
  getExtraLabel,
  getThemeLabel,
  getSegmentLabel,
  getTransferLabel,
  getExtraWithDependenciesLabel,
} from './labelGetters';

// Check if object is an attachment
export const isAttachment = (obj) => {
  return isObject(obj) && 'imageData' in obj && 'kind' in obj;
};

// Metadata fields carried by every translation row
const TRANSLATION_METADATA_FIELDS = [
  'id',
  'locale',
  '__typename',
  'createdAt',
  'updatedAt',
];

// Keys identifying a business row rather than a translation. The API diffs
// nested collections by semantic key and keeps `locale` last, so a row matched
// on one of these keys is never a translation even if it carries a locale.
const BUSINESS_KEY_FIELDS = [
  'experienceId',
  'extraId',
  'hotelId',
  'accreditationId',
  'experienceThemeId',
  'experienceSegmentId',
  'sustainableClusterId',
  'tripTemplateId',
  'periodId',
];

// Check if object is a translation.
// A translation is any locale-scoped row: history stores only the fields that
// changed, so a diffed translation may carry just `summary` or
// `includedServices` rather than a name or a description.
export const isTranslation = (obj) => {
  return (
    isObject(obj) &&
    'locale' in obj &&
    !BUSINESS_KEY_FIELDS.some((key) => key in obj) &&
    Object.keys(obj).some((key) => !TRANSLATION_METADATA_FIELDS.includes(key))
  );
};

// Check if object is an accreditation entry
export const isAccreditation = (obj, labels) => {
  if (!isObject(obj)) return false;
  if ('accreditationId' in obj) return true;
  // Check if it's an object with just id that matches accreditation labels
  if ('id' in obj && labels) {
    return !!getAccreditationLabel(obj.id, labels);
  }
  return false;
};

// Check if object is an extra entry
export const isExtra = (obj, labels) => {
  if (!isObject(obj)) return false;
  if ('extraId' in obj) return true;
  // Check if it's an object with just id that matches extra labels
  if ('id' in obj && labels) {
    return !!getExtraLabel(obj.id, labels);
  }
  return false;
};

// Check if object is a theme entry
export const isTheme = (obj, labels) => {
  if (!isObject(obj)) return false;
  if ('experienceThemeId' in obj) return true;
  // Check if it's an object with just id that matches theme labels
  if ('id' in obj && labels) {
    return !!getThemeLabel(obj.id, labels);
  }
  return false;
};

// Check if object is a segment entry
export const isSegment = (obj, labels) => {
  if (!isObject(obj)) return false;
  if ('experienceSegmentId' in obj) return true;
  // Check if it's an object with just id that matches segment labels
  if ('id' in obj && labels) {
    return !!getSegmentLabel(obj.id, labels);
  }
  return false;
};

// Check if object is a program entry
export const isProgram = (obj) => {
  return (
    isObject(obj) &&
    ('startPositionId' in obj || 'endPositionId' in obj) &&
    'translations' in obj
  );
};

// Check if object is a transfer entry
export const isTransfer = (obj, labels) => {
  if (!isObject(obj)) return false;
  if ('transferType' in obj) return true;
  // Check if it's an object with id that matches transfer labels
  if ('id' in obj && labels) {
    return !!getTransferLabel(obj.id, labels);
  }
  return false;
};

// Check if object is an extra with dependencies entry
export const isExtraWithDependencies = (obj, labels) => {
  if (!isObject(obj)) return false;
  // Has explicit dependency arrays
  if ('requiredExperiences' in obj || 'suggestedExperiences' in obj) {
    return 'extraId' in obj || 'extraName' in obj || 'id' in obj;
  }
  // Check if it's an object with id that matches extrasWithDependencies labels
  if ('id' in obj && labels) {
    return !!getExtraWithDependenciesLabel(obj.id, labels);
  }
  return false;
};

// Check if object is a program included option entry
export const isProgramIncludedOption = (obj) => {
  if (!isObject(obj)) return false;
  // Has experienceId or experienceName but NOT requiredExperiences/suggestedExperiences/required/suggested
  return (
    ('experienceId' in obj || 'experienceName' in obj) &&
    !('requiredExperiences' in obj) &&
    !('suggestedExperiences' in obj) &&
    !('required' in obj) &&
    !('suggested' in obj) &&
    !('hotelId' in obj) &&
    !('hotelName' in obj)
  );
};

// Check if object is a program suggested option entry
export const isProgramSuggestedOption = (obj) => {
  if (!isObject(obj)) return false;
  // Has experienceId/experienceName AND requiredExperiences/suggestedExperiences OR required/suggested
  return (
    ('experienceId' in obj || 'experienceName' in obj) &&
    ('requiredExperiences' in obj ||
      'suggestedExperiences' in obj ||
      'required' in obj ||
      'suggested' in obj)
  );
};

// Check if object is a program alternative hotel entry
export const isProgramAlternativeHotel = (obj) => {
  if (!isObject(obj)) return false;
  return 'hotelId' in obj || 'hotelName' in obj;
};

// Check if object is a POI (Point of Interest) entry
export const isPoi = (obj) => {
  if (!isObject(obj)) return false;
  // POI must have lat/lon coordinates
  return 'lat' in obj && 'lon' in obj;
};

// Check if object is a period entry
export const isPeriod = (obj) => {
  if (!isObject(obj)) return false;
  return (
    'startAt' in obj &&
    'endAt' in obj &&
    ('allotments' in obj || 'price' in obj || 'state' in obj)
  );
};
